import React from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Loader2, FileSignature, AlertTriangle, Building2 } from "lucide-react";
import { API } from "../lib/api";

const TYPE_LABEL = {
  partnership: "Samarbejdsaftale",
  subscription: "Fast pris-aftale (abonnement)",
  adhoc: "Ad-hoc aftale",
};

/**
 * Public B2B agreement signing page.
 * URL: /b2b-underskriv/:token
 */
export default function B2BSignPage() {
  const { token } = useParams();
  const [loading, setLoading] = React.useState(true);
  const [data, setData] = React.useState(null);
  const [err, setErr] = React.useState(null);
  const [typedName, setTypedName] = React.useState("");
  const [accept, setAccept] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [signed, setSigned] = React.useState(null);

  React.useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`${API}/sign/b2b/${token}`);
        if (!res.ok) {
          const j = await res.clone().json().catch(() => ({}));
          throw new Error(j.detail || `Fejl ${res.status}`);
        }
        const d = await res.json();
        if (!alive) return;
        setData(d);
        if (d.status === "signed") setSigned({ signed_at: d.signed_at });
        setTypedName(d.agreement?.contact_name || "");
      } catch (e) {
        if (alive) setErr(e.message);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [token]);

  React.useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("status") === "mitid-success") {
      toast.success("Tak — du har underskrevet med MitID Erhverv!");
    }
  }, []);

  const submitTyped = async (e) => {
    e.preventDefault();
    if (typedName.trim().length < 2) { toast.error("Skriv venligst dit fulde navn"); return; }
    if (!accept) { toast.error("Du skal acceptere aftalen"); return; }
    setBusy(true);
    try {
      const res = await fetch(`${API}/sign/b2b/${token}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ typed_name: typedName.trim(), accept: true }),
      });
      const j = await res.clone().json().catch(() => ({}));
      if (!res.ok) throw new Error(j.detail || `Fejl ${res.status}`);
      setSigned({ signed_at: j.signed_at });
      toast.success("Tak — din underskrift er registreret!");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-brand-bg2"><Loader2 className="animate-spin text-brand-red" size={32} /></div>;
  if (err) return (
    <div className="min-h-screen flex items-center justify-center bg-brand-bg2 px-4">
      <div className="bg-white border border-brand p-10 max-w-md text-center" data-testid="b2b-sign-error">
        <AlertTriangle size={40} className="text-brand-red mx-auto" />
        <h1 className="font-display font-bold text-2xl text-brand-dark mt-4">Linket virker ikke</h1>
        <p className="text-brand-text2 mt-2 text-sm">{err}</p>
      </div>
    </div>
  );

  const a = data?.agreement || {};

  if (signed) return (
    <div className="min-h-screen flex items-center justify-center bg-brand-bg2 px-4 py-12">
      <div className="bg-white border border-brand p-10 max-w-xl text-center" data-testid="b2b-sign-success">
        <div className="w-16 h-16 bg-emerald-100 text-emerald-700 mx-auto flex items-center justify-center rounded-full">
          <CheckCircle2 size={36} />
        </div>
        <h1 className="font-display font-bold text-3xl text-brand-dark mt-6">Aftalen er underskrevet</h1>
        <p className="text-brand-text2 mt-3">Tak — vi har modtaget jeres underskrift kl. <strong>{new Date(signed.signed_at).toLocaleString("da-DK")}</strong>.</p>
        <p className="text-brand-text2 mt-4 text-sm">EMA Kurer ApS sender jer en bekræftelse på email og kontakter jer kort for opstart.</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-brand-bg2 px-4 py-10">
      <div className="max-w-3xl mx-auto" data-testid="b2b-sign-page">
        <div className="bg-brand-dark text-white p-8 sm:p-10">
          <div className="kicker text-white/70">EMA Kurer ApS · B2B-aftale</div>
          <h1 className="font-display font-bold text-3xl sm:text-4xl tracking-tight mt-3">{TYPE_LABEL[a.agreement_type] || a.agreement_type}</h1>
          <p className="text-white/80 mt-4 text-sm sm:text-base leading-relaxed">
            Mellem <strong>EMA Kurer ApS</strong> og <strong>{a.customer_company}</strong>
          </p>
        </div>

        <div className="bg-white border border-brand p-7 sm:p-10">
          <div className="kicker flex items-center gap-2"><Building2 size={14}/> Parter</div>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <div className="border border-brand p-3">
              <div className="text-brand-text2 text-xs">Leverandør</div>
              <div className="font-semibold text-brand-dark">EMA Kurer ApS</div>
              <div className="text-xs text-brand-text2 mt-1">CVR 46465717<br/>Ullehøj 35, 4140 Borup<br/>Direktør: Sylwia Landowska<br/>Kontakt: Firat Cagler</div>
            </div>
            <div className="border border-brand p-3">
              <div className="text-brand-text2 text-xs">Kunde</div>
              <div className="font-semibold text-brand-dark">{a.customer_company}</div>
              <div className="text-xs text-brand-text2 mt-1">CVR {a.customer_cvr}<br/>{a.customer_address || "—"}<br/>Kontakt: {a.contact_name}</div>
            </div>
          </div>

          <div className="kicker mt-7">Vilkår — kort sammendrag</div>
          <ul className="mt-2 space-y-2 text-sm text-brand-dark leading-relaxed list-disc list-inside">
            {a.agreement_type === "subscription" && (
              <>
                <li><strong>Pris:</strong> {a.monthly_price} DKK/måned ({a.price_includes || "jf. aftalens omfang"}) ekskl. moms</li>
                <li><strong>Fakturering:</strong> Månedligt forud, 8 dages betalingsfrist</li>
                <li><strong>Løbetid:</strong> Start {a.start_date || "—"}, 1 måneds opsigelse</li>
              </>
            )}
            {a.agreement_type === "adhoc" && (
              <>
                <li><strong>Pris:</strong> {a.total_price} DKK ({a.price_includes || "éngangsbetaling"}) ekskl. moms</li>
                <li><strong>Fakturering:</strong> Ved aftalens udførelse, 8 dages betalingsfrist</li>
                <li><strong>Udførelse:</strong> {a.service_date || "Aftales særskilt"}</li>
              </>
            )}
            {a.agreement_type === "partnership" && (
              <>
                <li><strong>Ydelse:</strong> Kurérlevering på Sjælland efter aftalt prisaftale</li>
                <li><strong>Fakturering:</strong> Månedligt bagud baseret på leveringer, 8 dages betalingsfrist</li>
                <li><strong>Løbetid:</strong> Start {a.start_date || "—"}, 1 måneds opsigelse</li>
              </>
            )}
            <li><strong>Ansvar:</strong> Begrænset til pakkens dokumenterede værdi, max 5.000 DKK pr. forsendelse</li>
            <li><strong>GDPR:</strong> Persondata slettes efter 90 dage medmindre lovgivning kræver længere opbevaring</li>
            <li><strong>Tvister:</strong> Dansk ret · Retten i Roskilde</li>
          </ul>

          {a.custom_terms && (
            <>
              <div className="kicker mt-7">Særlige vilkår</div>
              <div className="mt-2 bg-brand-bg2 border border-brand p-3 text-sm whitespace-pre-wrap">{a.custom_terms}</div>
            </>
          )}

          {/* MitID Erhverv */}
          <div className="mt-8 border-t border-brand pt-6">
            <a
              href={`${API}/sign/b2b/${token}/mitid/initiate`}
              className="block bg-[#0060e6] hover:bg-[#0052cc] text-white text-center font-semibold py-4 px-6 transition-colors"
              data-testid="b2b-sign-mitid"
            >
              <div className="flex items-center justify-center gap-3">
                <svg width="28" height="28" viewBox="0 0 32 32" aria-hidden="true">
                  <rect x="2" y="6" width="28" height="20" rx="3" fill="#fff" />
                  <text x="16" y="20" textAnchor="middle" fontSize="9" fontWeight="700" fill="#0060e6" fontFamily="Arial">MitID</text>
                </svg>
                Underskriv med MitID Erhverv
              </div>
              <div className="text-xs font-normal text-white/85 mt-1">Anbefalet — kræver du er tegningsberettiget for CVR {a.customer_cvr}</div>
            </a>

            <div className="my-6 flex items-center gap-3">
              <div className="flex-1 h-px bg-brand"></div>
              <span className="text-xs uppercase tracking-wider text-brand-text2 font-semibold">Eller</span>
              <div className="flex-1 h-px bg-brand"></div>
            </div>

            <form onSubmit={submitTyped}>
              <label className="label-flat">Dit fulde navn <span className="text-brand-red">*</span></label>
              <input
                className="input-flat font-display text-lg italic"
                value={typedName}
                onChange={(e) => setTypedName(e.target.value)}
                data-testid="b2b-sign-name"
                required
              />
              <label className="flex items-start gap-3 mt-4 cursor-pointer bg-brand-bg2 border border-brand p-3 text-sm">
                <input type="checkbox" checked={accept} onChange={() => setAccept((v) => !v)} className="mt-1" data-testid="b2b-sign-accept" />
                <span>Jeg har læst og accepterer aftalen på vegne af <strong>{a.customer_company}</strong> som tegningsberettiget person.</span>
              </label>
              <button
                type="submit"
                className="btn-secondary mt-4 w-full sm:w-auto justify-center"
                disabled={busy || typedName.trim().length < 2 || !accept}
                data-testid="b2b-sign-typed-submit"
              >
                {busy ? <><Loader2 className="animate-spin" size={14} /> Underskriver…</> : <><FileSignature size={14} /> Underskriv med navn</>}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
